import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import logger from '../utils/logger';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const ConsultantInterviewMode = () => {
  const { id: assessmentId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [assessment, setAssessment] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [responses, setResponses] = useState({});
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [notes, setNotes] = useState('');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  
  const autoSaveInterval = useRef(null);

  useEffect(() => {
    startInterview();
    
    // Setup auto-save every 30 seconds
    autoSaveInterval.current = setInterval(() => {
      autoSaveProgress();
    }, 30000);
    
    return () => {
      if (autoSaveInterval.current) {
        clearInterval(autoSaveInterval.current);
      }
    };
  }, [assessmentId]);

  const startInterview = async () => {
    try {
      setLoading(true);
      setError('');

      const config = {
        withCredentials: true  // For HttpOnly cookies
      };

      const response = await axios.get(
        `${API_URL}/api/consultant/interview/${assessmentId}/start`,
        config
      );

      setAssessment(response.data.assessment);
      setQuestions(response.data.questions || []);
      
      // Load existing responses if any
      if (response.data.assessment.answers) {
        const existingResponses = {};
        response.data.assessment.answers.forEach(answer => {
          existingResponses[answer.question_id] = answer.answer;
        });
        setResponses(existingResponses);
      }
    } catch (err) {
      logger.error('Error starting interview:', err);
      setError(err.response?.data?.detail || 'Failed to start interview');
    } finally {
      setLoading(false);
    }
  };

  const autoSaveProgress = async () => {
    if (Object.keys(responses).length === 0) return;
    
    try {
      setSaving(true);
      
      const config = {
        headers: {
          'Content-Type': 'application/json'
        },
        withCredentials: true  // For HttpOnly cookies
      };

      const formattedResponses = Object.entries(responses).map(([questionId, answer]) => ({
        question_id: questionId,
        answer: answer
      }));

      await axios.post(
        `${API_URL}/api/consultant/interview/${assessmentId}/autosave`,
        { responses: formattedResponses },
        config
      );

      setLastSaved(new Date());
    } catch (err) {
      logger.error('Auto-save failed:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleResponseChange = (questionId, value) => {
    setResponses(prev => ({
      ...prev,
      [questionId]: value
    }));
  };

  const handleSubmit = async () => {
    try {
      setSubmitting(true);
      setError('');

      const config = {
        headers: {
          'Content-Type': 'application/json'
        },
        withCredentials: true  // For HttpOnly cookies
      };

      const formattedResponses = Object.entries(responses).map(([questionId, answer]) => ({
        question_id: questionId,
        answer: answer
      }));

      await axios.post(
        `${API_URL}/api/consultant/interview/${assessmentId}/submit`,
        {
          responses: formattedResponses,
          notes: notes
        },
        config
      );

      // Clear auto-save interval
      if (autoSaveInterval.current) {
        clearInterval(autoSaveInterval.current);
      }

      // Navigate to results
      navigate(`/consultant/results/${assessmentId}`);
    } catch (err) {
      logger.error('Error submitting interview:', err);
      setError(err.response?.data?.detail || 'Failed to submit interview');
    } finally {
      setSubmitting(false);
    }
  };

  const handlePrevious = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(currentQuestionIndex - 1);
    }
  };

  const handleNext = () => {
    if (currentQuestionIndex < questions.length - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
    }
  };

  const progressPercentage = questions.length > 0 
    ? Math.round((Object.keys(responses).length / questions.length) * 100)
    : 0;

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading interview...</p>
        </div>
      </div>
    );
  }

  if (error && !assessment) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 max-w-md">
          <h3 className="text-red-800 font-semibold mb-2">Error</h3>
          <p className="text-red-700">{error}</p>
          <button
            onClick={() => navigate('/consultant/dashboard')}
            className="mt-4 text-emerald-600 hover:text-emerald-700"
          >
            ← Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  const currentQuestion = questions[currentQuestionIndex];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Interview Mode</h1>
              <p className="text-gray-600">
                {assessment?.facility_name} - {assessment?.industry_name}
              </p>
            </div>
            
            <div className="flex items-center space-x-4">
              {/* Auto-save indicator */}
              <div className="text-sm text-gray-600">
                {saving ? (
                  <span className="flex items-center">
                    <svg className="animate-spin h-4 w-4 mr-2" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    Saving...
                  </span>
                ) : lastSaved ? (
                  <span className="text-green-600">
                    ✓ Saved {new Date(lastSaved).toLocaleTimeString()}
                  </span>
                ) : null}
              </div>
              
              <button
                onClick={() => navigate('/consultant/dashboard')}
                className="text-gray-600 hover:text-gray-800"
              >
                Exit
              </button>
            </div>
          </div>
          
          {/* Progress Bar */}
          <div className="mt-4">
            <div className="flex justify-between text-sm text-gray-600 mb-1">
              <span>Progress: {Object.keys(responses).length}/{questions.length} questions</span>
              <span>{progressPercentage}%</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className="bg-emerald-600 h-2 rounded-full transition-all"
                style={{ width: `${progressPercentage}%` }}
              ></div>
            </div>
          </div>
        </div>
      </div>

      {/* Split Screen Layout */}
      <div className="max-w-7xl mx-auto px-4 py-6">
        <div className="grid grid-cols-2 gap-6" style={{ height: 'calc(100vh - 220px)' }}>
          {/* Left Side - Questions */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden flex flex-col">
            <div className="p-6 border-b border-gray-200 bg-gray-50">
              <h2 className="text-lg font-semibold text-gray-900">Questions</h2>
              <p className="text-sm text-gray-600">Question {currentQuestionIndex + 1} of {questions.length}</p>
            </div>
            
            <div className="flex-1 overflow-y-auto p-6">
              {currentQuestion && (
                <div>
                  <div className="mb-6">
                    <span className="inline-block px-3 py-1 bg-emerald-100 text-emerald-800 rounded-full text-xs font-medium mb-3">
                      {currentQuestion.category || 'General'}
                    </span>
                    <h3 className="text-xl font-medium text-gray-900 mb-4">
                      {currentQuestion.question_text}
                    </h3>
                  </div>

                  {/* Response Input */}
                  <div className="space-y-4">
                    {currentQuestion.question_type === 'yes_no' && (
                      <div className="space-y-2">
                        {['Yes', 'No'].map(option => (
                          <label
                            key={option}
                            className="flex items-center p-3 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50"
                          >
                            <input
                              type="radio"
                              name={`question_${currentQuestion.id}`}
                              value={option}
                              checked={responses[currentQuestion.id] === option}
                              onChange={(e) => handleResponseChange(currentQuestion.id, e.target.value)}
                              className="mr-3"
                            />
                            <span>{option}</span>
                          </label>
                        ))}
                      </div>
                    )}

                    {currentQuestion.question_type === 'multiple_choice' && currentQuestion.options && (
                      <div className="space-y-2">
                        {currentQuestion.options.map(option => (
                          <label
                            key={option}
                            className="flex items-center p-3 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50"
                          >
                            <input
                              type="radio"
                              name={`question_${currentQuestion.id}`}
                              value={option}
                              checked={responses[currentQuestion.id] === option}
                              onChange={(e) => handleResponseChange(currentQuestion.id, e.target.value)}
                              className="mr-3"
                            />
                            <span>{option}</span>
                          </label>
                        ))}
                      </div>
                    )}

                    {currentQuestion.question_type === 'text' && (
                      <textarea
                        value={responses[currentQuestion.id] || ''}
                        onChange={(e) => handleResponseChange(currentQuestion.id, e.target.value)}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
                        rows={4}
                        placeholder="Enter your response..."
                      />
                    )}

                    {currentQuestion.question_type === 'numeric' && (
                      <input
                        type="number"
                        value={responses[currentQuestion.id] || ''}
                        onChange={(e) => handleResponseChange(currentQuestion.id, e.target.value)}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
                        placeholder="Enter a number..."
                      />
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Navigation */}
            <div className="p-6 border-t border-gray-200 bg-gray-50">
              <div className="flex justify-between">
                <button
                  onClick={handlePrevious}
                  disabled={currentQuestionIndex === 0}
                  className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  ← Previous
                </button>
                <button
                  onClick={handleNext}
                  disabled={currentQuestionIndex === questions.length - 1}
                  className="px-6 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next →
                </button>
              </div>
            </div>
          </div>

          {/* Right Side - Responses & Notes */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden flex flex-col">
            <div className="p-6 border-b border-gray-200 bg-gray-50">
              <h2 className="text-lg font-semibold text-gray-900">Session Notes & Summary</h2>
            </div>
            
            <div className="flex-1 overflow-y-auto p-6 space-y-6">
              {/* Consultant Notes */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Consultant Notes
                </label>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  rows={6}
                  placeholder="Add notes about this interview session..."
                />
              </div>

              {/* Response Summary */}
              <div>
                <h3 className="text-sm font-medium text-gray-700 mb-3">Response Summary</h3>
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {questions.map((q, idx) => (
                    <div
                      key={q.id}
                      className={`p-3 rounded-lg cursor-pointer ${
                        responses[q.id] 
                          ? 'bg-emerald-50 border border-emerald-200' 
                          : 'bg-gray-50 border border-gray-200'
                      }`}
                      onClick={() => setCurrentQuestionIndex(idx)}
                    >
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <p className="text-sm font-medium text-gray-900">
                            Q{idx + 1}: {q.question_text.substring(0, 60)}...
                          </p>
                          {responses[q.id] && (
                            <p className="text-xs text-gray-600 mt-1">
                              Answer: {String(responses[q.id]).substring(0, 40)}...
                            </p>
                          )}
                        </div>
                        {responses[q.id] && (
                          <span className="text-emerald-600 ml-2">✓</span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Submit Button */}
            <div className="p-6 border-t border-gray-200 bg-gray-50">
              {error && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-800 text-sm">
                  {error}
                </div>
              )}
              
              <button
                onClick={handleSubmit}
                disabled={submitting || Object.keys(responses).length === 0}
                className="w-full px-6 py-3 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium"
              >
                {submitting ? 'Submitting...' : 'Submit Interview & Generate Report'}
              </button>
              
              <p className="text-xs text-gray-500 mt-2 text-center">
                {Object.keys(responses).length}/{questions.length} questions answered
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConsultantInterviewMode;
